import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { 
  Lightbulb, 
  ArrowLeft, 
  ArrowRight,
  Save,
  Home,
  Check
} from "lucide-react";
// animations removed
import { toast } from "sonner";

// Step Components - NEW ORDER
import ProblemStep from "@/components/wizard/ProblemStep";
import IdeaMapStep from "@/components/wizard/IdeaMapStep";
import ConceptStep from "@/components/wizard/ConceptStep";
import ValuePropositionStep from "@/components/wizard/ValuePropositionStep";
import BusinessModelStep from "@/components/wizard/BusinessModelStep";
import PrototypeStep from "@/components/wizard/PrototypeStep";
import PresentationStep from "@/components/wizard/PresentationStep";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectWizard = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // NEW STEP ORDER: Problem > Ideas > Concept > VPC > BMC > Prototype > Pitch
  const STEPS = [
    { id: 1, label: t('wizard_problem'), short: "P" },
    { id: 2, label: t('wizard_ideas'), short: "I" },
    { id: 3, label: t('wizard_concept'), short: "C" },
    { id: 4, label: t('wizard_value_prop'), short: "VP" },
    { id: 5, label: t('wizard_business_model'), short: "BM" },
    { id: 6, label: t('wizard_prototype'), short: "Pr" },
    { id: 7, label: t('wizard_pitch'), short: "Pi" },
  ];

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await fetch(`${API}/projects/${projectId}`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setProject(data);
          setCurrentStep(data.current_step || 1);
        } else {
          toast.error(t('common_error'));
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error fetching project:", error);
        toast.error(t('common_error'));
        navigate("/dashboard");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProject();
  }, [projectId, navigate, t]);

  // Save project
  const saveProject = useCallback(async (updates, showToast = true) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const updatedProject = await response.json();
        setProject(updatedProject);
        if (showToast) toast.success(t('common_success'));
        return true;
      } else {
        toast.error(t('common_error'));
        return false;
      }
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error(t('common_error'));
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [projectId, t]);

  // Navigation
  const goToStep = async (step) => {
    if (step < 1 || step > 7) return;
    
    // Save current step before navigating
    await saveProject({ current_step: step }, false);
    setCurrentStep(step);
  };

  // NEW ORDER: Check what's needed for each step
  const canGoNext = () => {
    switch (currentStep) {
      case 1: return project?.problem_statement?.trim(); // Problem
      case 2: return project?.selected_idea; // Ideas
      case 3: return project?.concept; // Concept
      case 4: return project?.value_proposition; // VPC
      case 5: return project?.business_model; // BMC
      case 6: return project?.prototype_prompt; // Prototype
      default: return true;
    }
  };

  // Render step content - NEW ORDER
  const renderStep = () => {
    const stepProps = {
      project,
      setProject,
      saveProject,
      goToStep,
    };

    switch (currentStep) {
      case 1: return <ProblemStep {...stepProps} />;
      case 2: return <IdeaMapStep {...stepProps} />;
      case 3: return <ConceptStep {...stepProps} />;
      case 4: return <ValuePropositionStep {...stepProps} />;
      case 5: return <BusinessModelStep {...stepProps} />;
      case 6: return <PrototypeStep {...stepProps} />;
      case 7: return (
        <PresentationStep
          {...stepProps}
          onFinalize={async () => {
            // Save everything and mark as completed before going back to dashboard
            await saveProject({
              current_step: 7,
              status: "completed",
              language: undefined,
            }, false);
          }}
        />
      );
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Back */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-stone-600 hover:text-stone-900"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-semibold text-stone-900 hidden sm:inline truncate max-w-[200px]">
                  {project?.title || "Project"}
                </span>
              </div>
            </div>

            {/* Step Indicator */}
            <div className="hidden md:flex items-center gap-1">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => goToStep(step.id)}
                    disabled={step.id > currentStep + 1}
                    className={`step-dot ${
                      step.id === currentStep
                        ? "active"
                        : step.id < currentStep
                        ? "completed"
                        : "inactive"
                    }`}
                    data-testid={`step-${step.id}-btn`}
                  >
                    {step.id < currentStep ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step.short
                    )}
                  </button>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`step-line ${
                        step.id < currentStep ? "active" : ""
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Step Indicator */}
            <div className="md:hidden flex items-center gap-2">
              <span className="text-sm font-medium text-stone-600">
                {t('dashboard_step')} {currentStep}/7
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-stone-600 hover:text-stone-900"
                data-testid="home-btn"
              >
                <Home className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveProject({ current_step: currentStep })}
                disabled={isSaving}
                className="btn-secondary hidden sm:flex"
                data-testid="save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? t('common_saving') : t('nav_save')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar (Mobile) */}
      <div className="md:hidden bg-white border-b border-stone-200">
        <div className="h-1 bg-stone-100">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(currentStep / 7) * 100}%` }}
          />
        </div>
        <div className="px-4 py-2 text-sm text-stone-600">
          {STEPS[currentStep - 1]?.label}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full">
          {renderStep()}
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="bg-white border-t border-stone-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => goToStep(currentStep - 1)}
              disabled={currentStep === 1}
              className="btn-secondary"
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('nav_previous')}
            </Button>

            <div className="text-sm text-stone-500">
              {STEPS[currentStep - 1]?.label}
            </div>

            <Button
              onClick={() => goToStep(currentStep + 1)}
              disabled={currentStep === 7 || !canGoNext()}
              className="btn-primary"
              data-testid="next-step-btn"
            >
              {currentStep === 7 ? t('common_success') : t('nav_next')}
              {currentStep < 7 && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProjectWizard;
